import { useState, useEffect } from 'react'
import {
  CLASS_COLORS, buildClassifiedGroups, getLayers,
  confidenceColor, layerNameColor, toDisplayName,
  QBIQ_LAYER_NAMES, QLINE_ORDER, isQLine,
  hideClassKey, toggleClassVisibility, soloSubKey,
} from './utils'
import {
  EyeOn, EyeOff, PlusIcon, IsolateIcon,
  IconChevronRight, IconChevronUp, IconExpandTriangle, IconApproveCheck,
  IconAutoRemap, IconDownload,
} from './shared/assets/icons'
import { PLAUSIBLE_M } from './uploadUnits'

// ── Single flat layer row (Original / Preprocessed) ───────────────────────────
function FlatLayerRow({ layer, isVis, isFocused, isSelected, isSolo, onToggle, onFocus, onIsolate, layerStat, onMoveToLayer, hasSelection, isShiftSelected, onShiftSelect, onLayerClick }) {
  return (
    <div
      className={`layer-row ${isVis ? '' : 'dim'} ${isFocused ? 'focused' : ''} ${isSelected ? 'sel-highlight' : ''} ${isShiftSelected ? 'shift-sel' : ''}`}
      onClick={(e) => { if (e.shiftKey) { e.stopPropagation(); onShiftSelect?.(layer) } else { onLayerClick?.(layer); onFocus?.(layer) } }}
      title={isShiftSelected ? 'Shift-selected — actions apply to all selected layers' : 'Click to focus / dim other layers'}
    >
      <span className="layer-bar" style={{ background: layerNameColor(layer) }} />
      <span className="layer-name">{layer}</span>
      <button className="icon-btn small" onClick={e => { e.stopPropagation(); onToggle(layer) }}>
        {isVis ? <EyeOn /> : <EyeOff />}
      </button>
      <button className={`icon-btn small isolate-btn ${isSolo ? 'active' : ''}`}
        title={isSolo ? 'Click to un-isolate' : 'Isolate this layer only'}
        onClick={e => { e.stopPropagation(); onIsolate?.(layer) }}>
        <IsolateIcon />
      </button>
      {layerStat?.bestClass && (
        <span className="layer-qbiq-chip" style={{ background: CLASS_COLORS[layerStat.bestClass] || '#6b7280' }}>
          {toDisplayName(layerStat.bestClass)}
        </span>
      )}
      {layerStat?.avgScore != null && (
        <span className="conf-badge" style={{ color: confidenceColor(layerStat.avgScore) }}>
          {layerStat.avgScore}%
        </span>
      )}
      {hasSelection && onMoveToLayer && (
        <button className="move-to-layer-btn" title={`Move selected to ${layer}`}
          onClick={e => { e.stopPropagation(); onMoveToLayer(layer) }}>
          <PlusIcon width="10" height="10" strokeLinecap="round" />
        </button>
      )}
    </div>
  )
}

// ── Sublayer row under a qbiq approval group ───────────────────────────────────
function ApprovalSublayerRow({ sub, groupColor, onSublayerClick, isActive, onIsolate, isSolo, soloKey, isPrecleaned, isSelected }) {
  return (
    <div
      className={`layer-row sublayer-row ${isActive ? 'highlighted' : ''} ${isSelected ? 'sel-highlight' : ''}`}
      aria-current={isSelected ? 'true' : undefined}
      style={{ cursor: onSublayerClick || onIsolate ? 'pointer' : 'default' }}
      onClick={() => onSublayerClick?.(sub, groupColor)}
      title={onSublayerClick ? 'Click to see confidence breakdown' : undefined}
    >
      <span className="sublayer-indent" />
      <span className="layer-bar" style={{ background: groupColor, opacity: 0.5 }} />
      <span className="layer-name" style={{ fontSize: 11, color: '#475569' }}>{sub.name}</span>
      {isPrecleaned && <span className="preclean-chip" title="classify-agent hid these from the vision model">pre-cleaned</span>}
      {sub.avgScore != null && (
        <span className="conf-badge" style={{ color: confidenceColor(sub.avgScore) }}>{sub.avgScore}%</span>
      )}
      {onIsolate && (
        <button className={`icon-btn small isolate-btn ${isSolo ? 'active' : ''}`}
          title={isSolo ? 'Click to un-isolate' : 'Isolate this sublayer only'}
          onClick={e => { e.stopPropagation(); onIsolate(soloKey) }}>
          <IsolateIcon />
        </button>
      )}
      {onSublayerClick && (
        <IconChevronRight width="9" height="9" stroke="#94a3b8" strokeWidth="2" style={{ flexShrink: 0, marginRight: 2 }} />
      )}
    </div>
  )
}

// ── Classified qbiq layer row with approval checkbox ──────────────────────────
function QbiqApprovalRow({
  cls, group, isApproved, onToggleApprove,
  visibleLayers, onToggleGroup, focusedLayer, onFocus,
  hasSelection, onMoveToLayer, isSelected, isSolo, onIsolate,
  onSublayerClick, activePopupName,
  soloLayers, onSublayerIsolate, selectedSublayers,
  isShiftSelected, onShiftSelect, onSingleSelect, precleanLayers,
}) {
  const [open, setOpen] = useState(false)
  const color            = CLASS_COLORS[cls] || '#9ca3af'
  const displayName      = toDisplayName(cls)
  const isNonApprovable  = isQLine(cls) || cls === 'Untagged' || cls === '0'  // no checkbox for these
  const isNonMoveable    = isQLine(cls) || cls === 'Untagged'                  // "0" is moveable
  const sublayers        = group?.sublayers ?? []
  const qlineGroups = group?.qlineGroups ?? []
  // Protected classes ('Interior Design', 'Furniture') use their class name as the visibility key
  // so that hiding another group's DXF sublayer cannot accidentally hide them.
  const allKeys     = group?.isProtectedClass ? [cls] : sublayers.map(s => s.name)
  const classHidden = visibleLayers.has(hideClassKey(cls))
  const visCount    = allKeys.filter(k => visibleLayers.has(k)).length
  const allVis      = !classHidden && (allKeys.length === 0 || visCount === allKeys.length)
  const someVis     = !classHidden && visCount > 0
  const isFoc       = focusedLayer === cls
  const hasChildren = sublayers.length > 0 || qlineGroups.length > 0

  return (
    <div>
      <div
        className={`layer-row class-row ${someVis || !allKeys.length ? '' : 'dim'} ${isFoc ? 'focused' : ''} ${isSelected ? 'sel-highlight' : ''} ${isShiftSelected ? 'shift-sel' : ''}`}
        onClick={(e) => { if (e.shiftKey) { e.stopPropagation(); onShiftSelect?.(cls) } else { onSingleSelect?.(cls); onFocus(cls) } }}
        title={isShiftSelected ? 'Shift-selected — actions apply to all selected layers' : `Focus ${displayName}`}
      >
        <span className="layer-bar" style={{ background: color }} />

        {/* Expand arrow */}
        {hasChildren
          ? <button className={`expand-btn ${open ? 'open' : ''}`}
              aria-label={`Source layers for ${displayName}`} aria-expanded={open}
              onClick={e => { e.stopPropagation(); setOpen(o => !o) }}>
              <IconExpandTriangle />
            </button>
          : <span style={{ width: 14, flexShrink: 0 }} />
        }

        <span className="layer-name">{displayName}</span>

        {/* Confidence % for this qbiq layer */}
        {group?.avgScore != null && (
          <span className="conf-badge" style={{ color: confidenceColor(group.avgScore) }}>
            {group.avgScore}%
          </span>
        )}

        {/* Count badge */}
        {sublayers.length > 0 && (
          <span className="sublayer-count-badge">{sublayers.length}</span>
        )}

        {/* Eye toggle */}
        <button className="icon-btn small" onClick={e => {
          e.stopPropagation()
          if (allKeys.length === 0) return
          onToggleGroup(cls, allKeys, allVis)
        }}>
          {allVis ? <EyeOn /> : <EyeOff />}
        </button>

        {/* Isolate button */}
        <button className={`icon-btn small isolate-btn ${isSolo ? 'active' : ''}`}
          title={isSolo ? 'Click to un-isolate' : 'Isolate this layer only'}
          onClick={e => { e.stopPropagation(); onIsolate?.(cls) }}>
          <IsolateIcon />
        </button>

        {/* Move selected to this layer — hidden for Q-lines and Untagged, but allowed for "0" */}
        {hasSelection && !isNonMoveable && (
          <button className="move-to-layer-btn" title={`Move selected to ${toDisplayName(cls)}`}
            onClick={e => { e.stopPropagation(); onMoveToLayer?.(cls) }}>
            <PlusIcon width="10" height="10" strokeLinecap="round" />
          </button>
        )}

        {/* Approval checkbox — hidden for Q-lines and Untagged */}
        {!isNonApprovable && (
          <button
            className={`layer-approve-circle ${isApproved ? 'approved' : ''}`}
            title={isApproved ? 'Approved' : 'Click to approve this layer'}
            onClick={e => { e.stopPropagation(); onToggleApprove(cls) }}>
            {isApproved && (
              <IconApproveCheck />
            )}
          </button>
        )}
      </div>

      {/* Expanded sublayers */}
      {open && (
        <>
          {sublayers.length === 0 && (
            <div className="layer-row sublayer-row" style={{ cursor: 'default', opacity: 0.45 }}>
              <span className="sublayer-indent" />
              <span style={{ fontSize: 11, color: '#94a3b8', padding: '0 4px' }}>No layers classified here</span>
            </div>
          )}
          {sublayers.map(sub => (
            <ApprovalSublayerRow key={sub.name} sub={sub} groupColor={color}
              onSublayerClick={(sub, color) => onSublayerClick?.({ ...sub, qbiqClass: cls }, color)}
              isActive={activePopupName === sub.name}
              isSelected={selectedSublayers?.has(soloSubKey(cls, sub.name))}
              onIsolate={onSublayerIsolate}
              soloKey={soloSubKey(cls, sub.name)} isSolo={soloLayers?.has(soloSubKey(cls, sub.name))}
              isPrecleaned={precleanLayers?.has(sub.name)} />
          ))}
          {qlineGroups.map(qg => (
            <div key={qg.qbiqClass}>
              <div className="layer-row qline-row" style={{ paddingLeft: 4 }}>
                <span className="qline-indent" />
                <span className="layer-bar" style={{ background: qg.color }} />
                <span className="layer-name" style={{ fontSize: 11 }}>{qg.displayName}</span>
                {qg.avgScore != null && (
                  <span className="conf-badge" style={{ color: confidenceColor(qg.avgScore) }}>{qg.avgScore}%</span>
                )}
              </div>
              {qg.sublayers.map(sub => (
                <ApprovalSublayerRow key={sub.name} sub={sub} groupColor={qg.color}
                  onSublayerClick={onSublayerClick}
                  isActive={activePopupName === sub.name}
                  isSelected={selectedSublayers?.has(soloSubKey(qg.qbiqClass, sub.name))}
                  onIsolate={onSublayerIsolate}
                  soloKey={soloSubKey(qg.qbiqClass, sub.name)} isSolo={soloLayers?.has(soloSubKey(qg.qbiqClass, sub.name))}
                  isPrecleaned={precleanLayers?.has(sub.name)} />
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  )
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
export default function Sidebar({
  activeView, setActiveView,
  uploadData, classifyData,
  visibleLayers, setVisibleLayers,
  focusedLayer, onFocusLayer,
  activeLayer, activeTool,
  onSublayerClick, activePopupName,
  onShowConfRemap,
  layerStats,
  approvedLayers, onToggleApprove,
  onFinish, isFinishing,
  selectedHandles, onMoveToLayer,
  selectedLayers, soloLayers, onIsolateLayer, selectionDetails = [],
  highlightEl,
  onClassify, isClassifying, agentBusy,
  precleanLayers, autoReviewOn, onAutoReviewChange,
  planSize, onOpenUnits, scaleLocked,
  onApproveMultiple, onIsolateMultiple,
  protectedLayers,
  shiftSelected, setShiftSelected,
}) {
  const selectedSublayers = new Set(selectionDetails.map(row => row.subKey))
  const hl = (id) => highlightEl === id ? 'hl-pulse' : ''
  const hasSelection = !!(selectedHandles?.size > 0)
  const isClassified = activeView === 'classified' && !!classifyData
  const isOriginalView = activeView === 'original'
  const [collapseKey, setCollapseKey] = useState(0)

  // Clear shift selection on tab change
  useEffect(() => { setShiftSelected(new Set()) }, [activeView, setShiftSelected])
  // Also clear shift selection when focus is cleared
  useEffect(() => { if (!focusedLayer) setShiftSelected(new Set()) }, [focusedLayer, setShiftSelected])

  const flatLayers = (() => {
    if (activeView === 'original')     return getLayers(uploadData?.original_manifest)
    if (activeView === 'preprocessed') return getLayers(uploadData?.cleaned_manifest)
    return []
  })()

  // Protected class rows for Preprocessed view: unique classes from protectedLayers map
  const protectedClasses = (() => {
    if (activeView !== 'preprocessed' || !protectedLayers) return []
    return [...new Set(Object.values(protectedLayers))]
  })()

  const groups = isClassified ? buildClassifiedGroups(classifyData.manifest) : []

  // Only main qbiq layers (excluding Untagged + Q-lines which have no checkboxes)
  const allQbiqKeys = QBIQ_LAYER_NAMES.filter(c => c !== 'Untagged' && c !== '0')

  const allApproved = allQbiqKeys.every(cls => approvedLayers?.has(cls))

  // Flat visibility keys (for show/hide all) — mirrors the key set App.jsx
  // builds on classify/tab-change, so Show All restores everything it hid.
  const allFlatKeys = isClassified
    ? [
        ...QLINE_ORDER,
        ...groups.flatMap(g => [
          g.qbiqClass,
          ...g.sublayers.map(s => s.name),
          ...g.qlineGroups.flatMap(q => [q.qbiqClass, ...q.sublayers.map(s => s.name)]),
        ]),
      ]
    : [...flatLayers, ...protectedClasses]
  const allHidden = allFlatKeys.every(k => !visibleLayers.has(k))

  const toggleLayer = (key) => setVisibleLayers(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next
  })

  const toggleGroup = (keys, allOn) => setVisibleLayers(prev => {
    const next = new Set(prev)
    for (const k of keys) allOn ? next.delete(k) : next.add(k)
    return next
  })

  // Map every qbiq class (including Q-Lines) → its sublayer visibility keys
  const clsToKeys = {}
  for (const g of groups) {
    clsToKeys[g.qbiqClass] = g.sublayers.map(s => s.name)
    for (const qg of (g.qlineGroups || [])) {
      clsToKeys[qg.qbiqClass] = qg.sublayers.map(s => s.name)
    }
  }

  // Eye toggle — always affects only the clicked group, not shift-selected groups
  const handleToggleGroupForCls = (cls, allKeys, allVis) => setVisibleLayers(prev =>
    toggleClassVisibility(prev, cls, allKeys, allVis, !!groups.find(g => g.qbiqClass === cls)?.isProtectedClass))

  // Approve toggle — bulk when shift-selected
  const handleToggleApproveForCls = (cls) => {
    if (shiftSelected.has(cls) && shiftSelected.size > 1) {
      const shouldApprove = !approvedLayers?.has(cls)
      onApproveMultiple([...shiftSelected].filter(c => !isQLine(c) && c !== 'Untagged' && c !== '0'), shouldApprove)
    } else {
      onToggleApprove(cls)
    }
  }

  // Isolate toggle — bulk when shift-selected
  const handleIsolateForCls = (cls) => {
    if (shiftSelected.has(cls) && shiftSelected.size > 1) {
      onIsolateMultiple?.([...shiftSelected], cls)
    } else {
      onIsolateLayer(cls)
    }
  }

  const handleLayerClick = (cls) => {
    setShiftSelected(prev => {
      const isSameSingle = prev?.size === 1 && prev.has(cls) && focusedLayer === cls
      return isSameSingle ? new Set() : new Set([cls])
    })
  }

  // Bulk-aware toggle for flat layer rows
  const handleFlatToggle = (layer) => {
    if (shiftSelected.has(layer) && shiftSelected.size > 1) {
      setVisibleLayers(prev => {
        const allVis = [...shiftSelected].every(l => prev.has(l))
        const next = new Set(prev)
        for (const l of shiftSelected) allVis ? next.delete(l) : next.add(l)
        return next
      })
    } else {
      toggleLayer(layer)
    }
  }

  // Bulk-aware isolate for flat layer rows
  const handleFlatIsolate = (layer) => {
    if (shiftSelected.has(layer) && shiftSelected.size > 1) {
      onIsolateMultiple?.([...shiftSelected], layer)
    } else {
      onIsolateLayer(layer)
    }
  }

  const handleShiftSelect = (cls) => {
    setShiftSelected(prev => {
      const next = new Set(prev)
      next.has(cls) ? next.delete(cls) : next.add(cls)
      return next
    })
  }

  return (
    <aside className="sidebar">
      {/* Tabs */}
      <div className="sidebar-tabs">
        <button className={`sidebar-tab ${activeView === 'classified' ? 'active' : ''} ${hl('tab-classified')}`}
          onClick={() => setActiveView('classified')} disabled={!classifyData}>Classified</button>
        <button className={`sidebar-tab ${activeView === 'preprocessed' ? 'active' : ''}`}
          onClick={() => setActiveView('preprocessed')} disabled={!uploadData}>Preprocessed</button>
        <button className={`sidebar-tab ${activeView === 'original' ? 'active' : ''} ${hl('tab-original')}`}
          onClick={() => setActiveView('original')} disabled={!uploadData}>Original</button>
      </div>

      {/* Controls */}
      {allFlatKeys.length > 0 && (
        <div className="sidebar-controls">
          <button className="ctrl-btn" onClick={() =>
            setVisibleLayers(allHidden ? new Set(allFlatKeys) : new Set())
          }>
            {allHidden ? <EyeOn /> : <EyeOff />}
            {allHidden ? 'Show All' : 'Hide All'}
          </button>
          {isClassified && (
            <button className="ctrl-btn" onClick={() => setCollapseKey(k => k + 1)}>
              <IconChevronUp />
              Collapse All
            </button>
          )}
          {focusedLayer && (
            <button className="ctrl-btn focus-clear" onClick={() => onFocusLayer?.(focusedLayer)}>
              ✕ Clear Focus
            </button>
          )}
        </div>
      )}

      {/* Active drawing layer banner */}
      {activeTool === 'draw' && activeView === 'classified' && (
        <div className="active-layer-bar">
          <span className="active-layer-label">Drawing on</span>
          <span className="active-layer-dot" style={{ background: CLASS_COLORS[activeLayer] || '#9ca3af' }} />
          <span className="active-layer-name">{toDisplayName(activeLayer)}</span>
        </div>
      )}

      {/* Layer list */}
      <div className="layer-list">
        {!uploadData && <p className="hint">Upload a file to see layers</p>}

        {/* Flat layers (Original — with stats / Preprocessed — no stats) */}
        {!isClassified && flatLayers.map(layer => (
          <FlatLayerRow key={layer} layer={layer}
            isVis={visibleLayers.has(layer)} isFocused={focusedLayer === layer}
            isSelected={selectedLayers?.has(layer)} isSolo={soloLayers?.has(layer)}
            layerStat={activeView === 'original' ? layerStats?.[layer] : undefined}
            onToggle={handleFlatToggle} onFocus={onFocusLayer} onIsolate={handleFlatIsolate}
            hasSelection={hasSelection} onMoveToLayer={onMoveToLayer}
            isShiftSelected={shiftSelected.has(layer)} onShiftSelect={handleShiftSelect}
            onLayerClick={handleLayerClick} />
        ))}

        {/* Protected class rows (Preprocessed only) */}
        {!isClassified && protectedClasses.map(cls => {
          const count = Object.values(protectedLayers).filter(v => v === cls).length
          return (
            <div
              key={cls}
              className={`layer-row ${visibleLayers.has(cls) ? '' : 'dim'} ${focusedLayer === cls ? 'focused' : ''}`}
              onClick={() => onFocusLayer?.(cls)}
              title="Click to focus / dim other layers"
            >
              <span className="layer-bar" style={{ background: CLASS_COLORS[cls] }} />
              <span className="layer-name">{cls} <span style={{ color: '#94a3b8', fontSize: 11 }}>({count})</span></span>
              <button className="icon-btn small" onClick={e => { e.stopPropagation(); toggleLayer(cls) }}>
                {visibleLayers.has(cls) ? <EyeOn /> : <EyeOff />}
              </button>
              <button className={`icon-btn small isolate-btn ${soloLayers?.has(cls) ? 'active' : ''}`}
                title={soloLayers?.has(cls) ? 'Click to un-isolate' : 'Isolate this layer only'}
                onClick={e => { e.stopPropagation(); onIsolateLayer?.(cls) }}>
                <IsolateIcon />
              </button>
            </div>
          )
        })}

        {/* Classified: main qbiq layers */}
        {isClassified && QBIQ_LAYER_NAMES.map(cls => {
          const group = groups.find(g => g.qbiqClass === cls)
          return (
            <QbiqApprovalRow key={`${cls}:${collapseKey}`} cls={cls} group={group}
              isApproved={approvedLayers?.has(cls) ?? false}
              onToggleApprove={handleToggleApproveForCls}
              visibleLayers={visibleLayers} onToggleGroup={handleToggleGroupForCls}
              focusedLayer={focusedLayer} onFocus={onFocusLayer}
              isSelected={selectedLayers?.has(cls)} isSolo={soloLayers?.has(cls)} onIsolate={handleIsolateForCls}
              hasSelection={hasSelection} onMoveToLayer={onMoveToLayer}
              onSublayerClick={onSublayerClick} activePopupName={activePopupName}
              selectedSublayers={selectedSublayers} soloLayers={soloLayers} onSublayerIsolate={onIsolateLayer} precleanLayers={precleanLayers}
              isShiftSelected={shiftSelected.has(cls)} onShiftSelect={handleShiftSelect}
              onSingleSelect={handleLayerClick} />
          )
        })}


        {/* Q-Lines section */}
        {isClassified && (
          <>
            <div className="layer-section-divider">Q-Lines</div>
            {QLINE_ORDER.map(cls => {
              const parentGroup = groups.find(g => g.qlineGroups?.some(q => q.qbiqClass === cls))
              const qlineGroup  = parentGroup?.qlineGroups?.find(q => q.qbiqClass === cls)
              const fakeGroup   = qlineGroup ? { sublayers: qlineGroup.sublayers, qlineGroups: [], avgScore: qlineGroup.avgScore } : undefined
              return (
                <QbiqApprovalRow key={`${cls}:${collapseKey}`} cls={cls} group={fakeGroup}
                  isApproved={approvedLayers?.has(cls) ?? false}
                  onToggleApprove={handleToggleApproveForCls}
                  visibleLayers={visibleLayers} onToggleGroup={handleToggleGroupForCls}
                  focusedLayer={focusedLayer} onFocus={onFocusLayer}
                  isSelected={selectedLayers?.has(cls)} isSolo={soloLayers?.has(cls)} onIsolate={handleIsolateForCls}
                  hasSelection={hasSelection} onMoveToLayer={onMoveToLayer}
                  onSublayerClick={onSublayerClick} activePopupName={activePopupName}
                  selectedSublayers={selectedSublayers} soloLayers={soloLayers} onSublayerIsolate={onIsolateLayer} precleanLayers={precleanLayers}
                  isShiftSelected={shiftSelected.has(cls)} onShiftSelect={handleShiftSelect}
                  onSingleSelect={handleLayerClick} />
              )
            })}
          </>
        )}

        {/* Auto-remap footer */}
        {isClassified && (
          <div className="conf-remap-footer">
            <button className="ctrl-btn conf-remap-trigger" onClick={onShowConfRemap}>
              <IconAutoRemap />
              Auto-Remap by Confidence
            </button>
          </div>
        )}
      </div>

      {/* Classify button */}
      {uploadData && !isClassified && (
        <div className="sidebar-classify-wrap">
          {planSize && (
            <button className={`plan-size-btn ${Math.max(planSize.w, planSize.h) < PLAUSIBLE_M[0] || Math.max(planSize.w, planSize.h) > PLAUSIBLE_M[1] ? 'warn' : ''}`}
              onClick={onOpenUnits} disabled={scaleLocked} title={scaleLocked ? 'Scale is locked while processing' : 'Drawing units and scale'}>
              Plan ≈ {planSize.w.toFixed(1)} × {planSize.h.toFixed(1)} m · <u>Units</u>
            </button>
          )}
          {onAutoReviewChange && (
            <label className="preclean-check">
              <input type="checkbox" checked={!!autoReviewOn} disabled={isClassifying}
                onChange={e => onAutoReviewChange(e.target.checked)} />
              Auto-review and fix with classify-agent
            </label>
          )}
          <button className={`btn btn-primary sidebar-classify-btn ${hl('classify')}`}
            onClick={onClassify} disabled={!uploadData || isClassifying || isOriginalView || agentBusy}
            title={isOriginalView ? 'Switch to the Preprocessed tab to classify'
              : agentBusy ? 'classify-agent is working — stop it or wait' : undefined}>
            {isClassifying ? <><span className="spin light" /> Classifying…</> : 'Classify'}
          </button>
        </div>
      )}

      {/* Finish classification button */}
      {isClassified && (
        <div className="finish-footer">
          <div className="finish-progress">
            <span className="finish-count">
              {approvedLayers?.size ?? 0} / {allQbiqKeys.length} approved
            </span>
            <div className="finish-bar-track">
              <div className="finish-bar-fill"
                style={{ width: `${((approvedLayers?.size ?? 0) / allQbiqKeys.length) * 100}%` }} />
            </div>
          </div>
          <button
            className={`btn btn-primary btn-sm finish-btn ${allApproved ? '' : 'disabled-soft'} ${hl('finish')}`}
            onClick={allApproved ? onFinish : undefined}
            disabled={!allApproved || isFinishing}
            title={allApproved ? 'Download classified DXF' : 'Approve all layers first'}
          >
            {isFinishing
              ? <><span className="spin light" /> Exporting…</>
              : <>
                  <IconDownload />
                  Finish &amp; Export
                </>
            }
          </button>
        </div>
      )}
    </aside>
  )
}
